// Light/dark theme, chosen per device in Settings. The script in index.html applies the saved choice
// before first paint (keep the two in step); this module applies changes while the app runs.
import { useSyncExternalStore } from "react";

export type ThemeChoice = "system" | "light" | "dark";

const KEY = "ck-theme";
/** Matches the header bar in each theme, for the browser/OS title bar. */
const BAR_COLOR = { light: "#ffffff", dark: "#131920" } as const;

const listeners = new Set<() => void>();
/** The choice made in this visit; null until changed, then it wins over a storage read that might fail. */
let current: ThemeChoice | null = null;

export function getThemeChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

function systemPrefersDark(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;
}

export function resolvedTheme(choice: ThemeChoice = getThemeChoice()): "light" | "dark" {
  return choice === "system" ? (systemPrefersDark() ? "dark" : "light") : choice;
}

export function applyTheme(choice: ThemeChoice = getThemeChoice()): void {
  const theme = resolvedTheme(choice);
  document.documentElement.dataset.theme = theme;
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute("content", BAR_COLOR[theme]);
}

export function setThemeChoice(choice: ThemeChoice): void {
  try {
    if (choice === "system") localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
  } catch {
    // Private mode: the choice lasts for this visit only.
  }
  current = choice;
  applyTheme(choice);
  for (const l of listeners) l();
}

/** Follows the OS setting while the choice is "System". */
export function watchSystemTheme(): void {
  if (typeof matchMedia !== "function") return;
  matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if ((current ?? getThemeChoice()) === "system") applyTheme("system");
  });
}

export function useThemeChoice(): ThemeChoice {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => current ?? getThemeChoice(),
  );
}
